import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { FolderWithPaginatedChildrenAndDocuments } from '@api/types';
import { FolderNode } from '@shared/layout/sidebar-menu/folder-menu/folder-menu.datasource';
import { ModalService } from '@shared/modal/modal.service';
import { FolderReferenceService } from '@shared/services/folder-reference.service';
import { ImportStatusService } from '@shared/services/import-status.service';
import { ImportDocument, ImportJob, ImportType } from '@shared/types/import';
import { SupportedMimeTypes } from '@shared/utils/file-type';
import { Subscription } from 'rxjs';

@Component({
  selector: 'rav-import-files',
  templateUrl: './import-files.component.html',
  styleUrls: ['./import-files.component.scss']
})
export class ImportFilesComponent implements OnInit, OnDestroy {
  /**
   * If there are any files that are too large to import (> 30MB) give user feedback as to why they can't
   */
  get filesTooLargeMessage(): string {
    return `${this.filesTooLarge.join(',')} exceed max file size of 30MB and cannot be imported.`;
  }

  /**
   * If there are any files that are not pdfs, give user feedback as to why they can't upload
   */
  get nonPdfFilesMessage(): string {
    return `${this.invalidFiles.join(',')} are not supported file types. Import PDF, JPEG, or TIFF documents.`;
  }

  selectedFolder: FolderNode;
  documentsForUpload: ImportDocument[] = [];
  isFileSelected = false;
  formGroup: FormGroup;
  jobs: ImportJob[] = [];
  hasCorrectMinFilename = true;
  hasCorrectMinFilenameMessage = '';

  modalOpen: Subscription;
  filesTooLarge: string[] = [];
  invalidFiles: string[] = [];
  errorMessage: string;
  inboxNode: FolderNode;
  topFolder: FolderWithPaginatedChildrenAndDocuments;
  topFolderId: string;
  subscription: Subscription = new Subscription();

  constructor(
    public modal: ModalService,
    private formBuilder: FormBuilder,
    private importStatusService: ImportStatusService,
    private folderReference: FolderReferenceService,
    private router: Router
  ) {
    this.formGroup = this.formBuilder.group({ file: [null, Validators.required] });
  }

  async ngOnInit() {
    const inbox = await this.folderReference.getInbox();
    this.inboxNode = new FolderNode(inbox.id, 'Inbox', 0, '', []);
    this.topFolder = await this.folderReference.getTopFolder();
    if (this.topFolder) this.topFolderId = this.topFolder.id;

    this.subscription.add(
      this.modal.open.subscribe((data) => {
        if (data && data.modalId === 'importFiles') {
          this.setCurrentFolder();
          this.resetProperties();
          this.formGroup = this.formBuilder.group({ file: [null, Validators.required] });
        }
      })
    );

    this.subscription.add(
      this.importStatusService.dragImportAnnounced$.subscribe(async (importObject) => {
        const { type, files, selectedFolder } = importObject;
        if (type === ImportType.File) {
          await this.setDocumentsForUpload(files);
          this.selectedFolder = selectedFolder;
          if (this.documentsForUpload.length < files.length) {
            const shouldContinue = confirm(
              'We only support PDF, JPEG, JPG or TIFF documents up to 30MB per file. Other types of files will not be imported.'
            );
            if (!shouldContinue || !this.documentsForUpload.length) {
              return;
            }
          }
          this.onImportButtonClicked();
        }
      })
    );
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    this.resetProperties();
  }

  /**
   * Resets all error and file handling properties upon launching the modal and when new file(s) are selected.
   */
  resetProperties() {
    this.documentsForUpload = [];
    this.filesTooLarge = [];
    this.invalidFiles = [];
    this.errorMessage = '';
    this.isFileSelected = false;
    this.hasCorrectMinFilename = true;
    this.hasCorrectMinFilenameMessage = '';
  }

  /**
   * When click the import button, create job for each documents and start import.
   */
  async onImportButtonClicked() {
    try {
      while (this.documentsForUpload.length) {
        const document = this.documentsForUpload.pop();
        const job = new ImportJob(null, '', 1, document.file.name, document, this.selectedFolder.id);
        this.jobs.push(job);
      }
      if (this.jobs.length > 0) {
        this.importStatusService.createNewJobs(this.jobs, ImportType.File);
        this.modal.close.next();
      }
    } catch (error) {
      // eslint-disable-next-line @typescript-eslint/quotes
      this.errorMessage = "There's been an error importing your files";
    }
  }

  /**
   * Sets current folder according to Url
   */

  async setCurrentFolder() {
    const urlPath = this.router.url.includes('?')
      ? this.router.url.substring(0, this.router.url.indexOf('?')).split('/')
      : this.router.url.split('/');

    if (this.topFolder.title.toLowerCase() === urlPath[1]) {
      const currentFolderName = urlPath[2] ? await this.folderReference.getFolderName(urlPath[2]) : undefined;
      if (currentFolderName) {
        this.selectedFolder = new FolderNode(urlPath[2], currentFolderName, 1, '', []);
      } else {
        this.selectedFolder = new FolderNode(this.topFolder.id, this.topFolder.title, 0, '', []);
      }
    } else {
      this.selectedFolder = this.inboxNode;
    }
  }

  /**
   * Triggered by the file input's change event. Builds formData objects for the selected files
   * for import and rejects invalid files.
   *
   * @param event the change event triggered after file selection
   */
  async filesSelected(event) {
    this.resetProperties();
    const fileList: File[] = event.target.files;
    this.validateFilenames(fileList);
    await this.setDocumentsForUpload(fileList);
  }

  folderSelected(node: FolderNode) {
    this.selectedFolder = node;
  }

  private getBasename(str: string): string {
    let base = str.substring(str.lastIndexOf('/') + 1).substring(str.lastIndexOf('\\') + 1);
    if (base.lastIndexOf('.') !== -1) base = base.substring(0, base.lastIndexOf('.'));
    return base;
  }

  private getBasenameWithExtension(str: string): string {
    return str.replace(/^.*[\\\/]/, '');
  }

  private validateFilenames(files: File[]): void {
    this.hasCorrectMinFilename = true;
    this.hasCorrectMinFilenameMessage = '';

    for (const file of files) {
      if (this.getBasename(file.name).length < 2) {
        this.hasCorrectMinFilename = false;
        this.hasCorrectMinFilenameMessage = `Title for "${this.getBasenameWithExtension(
          file.name
        )}" must be at least 2 characters.`;
      }
    }
  }

  /**
   * Builds formData objects for the selected files for import and rejects invalid files.
   *
   * @param fileList The file list that need to be pushed into upload documents list.
   */
  private async setDocumentsForUpload(fileList: File[]) {
    for (let i = 0; i < fileList.length; i++) {
      const file: File = fileList[i];
      let invalidFile = false;
      // files larger than 30MB not currently supported
      if (file.size > 30000000) {
        this.filesTooLarge.unshift(file.name);
        invalidFile = true;
      } else {
        switch (file.name.toLowerCase().substring(file.name.lastIndexOf('.'))) {
          case '.jpg':
          case '.jpeg':
            this.documentsForUpload.unshift({
              file: file,
              type: SupportedMimeTypes.JPEG
            });
            break;
          case '.tiff':
          case '.tif':
            this.documentsForUpload.unshift({
              file: file,
              type: SupportedMimeTypes.TIFF
            });
            break;
          case '.pdf':
            this.documentsForUpload.unshift({
              file: file,
              type: SupportedMimeTypes.PDF
            });
            break;
          default:
            this.invalidFiles.unshift(file.name);
            invalidFile = false;
            break;
        }
      }

      if (!invalidFile) this.isFileSelected = true;
    }
  }
}
